import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PROGRAM_PURPOSE_OPTIONS,
  ProgramPurpose
} from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, FormField, Modal, MultiSelect, Spacer, Typography } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { PurposeModalProps } from './modals.types'

export function PurposeModal({ program, selectedPurposes, setSelectedPurposes, refreshPrograms }: PurposeModalProps) {
  const { t } = useTranslation()
  const { validationSubmissionsService } = useServices()

  const [{ isPending, status }, { apiFunc: updateValidationSubmissionPurpose }] = useMutation(
    validationSubmissionsService.updateValidationSubmissionPurpose,
    undefined,
    undefined,
    { mutationKey: ['set-program-purpose', program.id] }
  )

  const isOpen = selectedPurposes !== null
  const isSuccess = status === 'success'
  const isError = status === 'error'

  const purposeOptions = useMemo(
    () =>
      PROGRAM_PURPOSE_OPTIONS.map(purpose => ({
        label: t(`programPurpose.${purpose}`),
        value: purpose
      })),
    [t]
  )

  const selectedPurposeOptions = useMemo(() => {
    const purposes = selectedPurposes ?? []

    return purposeOptions.filter(option => purposes.includes(option.value as ProgramPurpose))
  }, [purposeOptions, selectedPurposes])

  const handleClose = () => setSelectedPurposes(null)

  const handleCloseAndRefresh = () => {
    handleClose()
    refreshPrograms()
  }

  const handleSubmit = () => {
    updateValidationSubmissionPurpose({
      id: program.id,
      purposes: selectedPurposes ?? []
    })
  }

  const successModal = isSuccess && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.setProgramPurpose.success.title')}
      description={t('dialog.setProgramPurpose.success.description', {
        programName: program.name || t('typography.unnamedProgram')
      })}
      buttonText={t('button.close')}
      handleClose={handleCloseAndRefresh}
    />
  )

  const errorModal = isError && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.setProgramPurpose.error.title')}
      description={t('dialog.setProgramPurpose.error.description')}
      buttonText={t('button.done')}
      handleClose={handleCloseAndRefresh}
    />
  )

  const actionModal = !isSuccess && !isError && (
    <ActionModal
      title={t('dialog.setProgramPurpose.title')}
      primaryButtonText={t('button.setPurpose')}
      secondaryButtonText={t('button.cancel')}
      primaryBtnTestId="set-program-purpose"
      secondaryBtnTestId="set-program-purpose-cancel-button"
      type="submit"
      variant="primary"
      loading={isPending}
      disabled={false}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
    >
      <Typography variant="default-medium">{t('dialog.setProgramPurpose.description')}</Typography>
      <Spacer size={4} axis="vertical" />
      <FormField label={t('form.programPurpose')}>
        <MultiSelect
          placeholder={t('form.programPurposePlaceholder')}
          options={purposeOptions}
          defaultValue={selectedPurposeOptions}
          allowClear
          onChange={selectedOptions => {
            setSelectedPurposes(Array.from(new Set(selectedOptions.map(option => option.value as ProgramPurpose))))
          }}
        />
      </FormField>
    </ActionModal>
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || actionModal}
    </Modal>
  )
}
